// Shared karma-wallet read for GET /api/payments/user/{id}/wallet.
//
// A failed fetch must never be reported as "balance is zero" — 0 is a real,
// legitimate balance. Callers get an explicit status so an expired token or a
// dropped connection can never be rendered as a fact about the wallet.
import { getBackendUrl } from '../config/api';
import { authHeaders } from './authHeaders';

/**
 * @param {string|undefined} userId
 * @returns {Promise<{status: 'known'|'unauthenticated'|'error', balance: number|null}>}
 */
export async function fetchKarmaWallet(userId) {
  if (!userId) return { status: 'unauthenticated', balance: null };
  try {
    const res = await fetch(`${getBackendUrl()}/api/payments/user/${userId}/wallet`, {
      headers: authHeaders(),
      credentials: 'include',
    });
    if (res.status === 401 || res.status === 403) {
      return { status: 'unauthenticated', balance: null };
    }
    if (!res.ok) {
      return { status: 'error', balance: null };
    }
    // A 200 with a missing/null/non-numeric karma_balance is malformed, not
    // zero — `Number(x) || 0` would coerce that malformed body into a fact.
    // Number(null) is 0 (finite!), so null must be rejected explicitly before
    // the coercion, not caught by an isFinite check after it.
    const data = await res.json().catch(() => null);
    const raw = data?.karma_balance;
    if (raw === null || raw === undefined) {
      return { status: 'error', balance: null };
    }
    const n = Number(raw);
    if (!Number.isFinite(n)) {
      return { status: 'error', balance: null };
    }
    return { status: 'known', balance: n };
  } catch {
    return { status: 'error', balance: null };
  }
}

export default fetchKarmaWallet;
